import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, Globe, Smartphone, Monitor, MonitorSmartphone } from 'lucide-react';
// Use new unified product configuration
import { getProductsByCategory, getProductStyle, get12MonthProductId } from '../../config/homeProductsConfig';
// Import custom user icons
import { UserCountBadge, UserCountDisplay } from '../icons/UsersIcon';

export default function ProductGrid() {
 const { t } = useTranslation();
 const navigate = useNavigate();

 
 // 从配置中获取各类别产品
 const officialProducts = getProductsByCategory('official');
 const officialRenewalProducts = getProductsByCategory('official-renewal');
 const officialSharedProducts = getProductsByCategory('official-shared');
 const sharedProducts = getProductsByCategory('shared');
 const monkeyaiProducts = getProductsByCategory('monkeyai');

 // 获取图标组件
 const getIconComponent = (iconName) => {
 const iconMap = {
 'Globe': Globe,
 'Smartphone': Smartphone
 };
 return iconMap[iconName] || Globe;
 };

 const handleProductClick = (product) => {
 // 获取对应的12个月套餐产品ID
 const twelveMonthProductId = get12MonthProductId(product.productId);
 console.log('Product clicked, navigating to product details page:', {
 productID: product.productId,
 productType: product.type,
 twelveMonthProductId: twelveMonthProductId,
 redirectURL: `/product/${twelveMonthProductId}?type=${product.type}`
 });
 navigate(`/product/${twelveMonthProductId}?type=${product.type}`);
 };



 // 渲染产品列表的函数
 const renderProductList = (products, categoryName, categoryDescription, categoryType) => {
 if (products.length === 0) return null;

 return (
 <div className="mb-12 sm:mb-16">
 {/* 大类标题 */}
 <div className="mb-6 sm:mb-8">
 <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-ink-900 mb-2 tracking-tight">{categoryName}</h2>
 <p className="text-sm sm:text-base text-ink-600 mb-4">{categoryDescription}</p>
 <div className="w-12 h-px bg-ink-300 mt-6"></div>
 </div>


 {/* 产品列表 */}
 <div className="space-y-3">
 {products.map((product, index) => {
 const productStyle = getProductStyle(product.type);
 const IconComponent = getIconComponent(productStyle.icon);

 return (
 <div
 key={product.id}
 className="bg-cream-50 rounded-xl overflow-hidden border border-ink-200 hover:border-ink-900 transition-colors duration-200 group cursor-pointer relative"
 onClick={() => handleProductClick(product)}
 >
 <div className="p-4 sm:p-6 relative z-10">
 
 {/* 桌面端表格布局 */}
 <div className="hidden xl:grid xl:grid-cols-10 gap-4 items-center">
 
 {/* 第1列：产品图片和名称 */}
 <div className="xl:col-span-3 flex items-center gap-4">
 <div className="flex-shrink-0">
 <div className="relative w-16 h-16 sm:w-20 sm:h-20">
 <img
 src={product.image}
 alt={t(product.nameKey)}
 className="w-full h-full object-cover rounded-lg border border-ink-200"
 onError={(e) => {
 e.target.style.display = 'none';
 e.target.nextSibling.style.display = 'flex';
 }}
 />
 <div className="w-full h-full bg-cream-100 rounded-lg flex items-center justify-center border border-ink-200" style={{display: 'none'}}>
 <IconComponent className="w-8 h-8 text-ink-700" />
 </div>
 </div>
 </div>
 <div>
 <h3 className="font-semibold text-ink-900 group-hover:text-accent-600 transition-colors duration-200 text-sm sm:text-base">
 {t(product.nameKey)}
 </h3>
 <div className="text-xs text-ink-500 mt-1">
 {product.features.loginType === 'shared' ?
 (product.id === 'gpt-vip' ? t('homepage.productGrid.vipExclusive') : t('homepage.productGrid.privateConversation')) :
 (product.features.users === 1 ?
 t('homepage.productGrid.userCount.single') :
 t('homepage.productGrid.userCount.shared', { count: product.features.users })
 )
 }
 </div>
 </div>
 </div>

 {/* 第2列：价格 */}
 <div className="xl:col-span-1 text-center">
 <div className="flex flex-col items-center">
 <div className="text-2xl font-serif text-ink-900 tracking-tight">
 {product.currency}{product.price}
 </div>
 <div className="text-xs text-ink-500 mt-1">
 <span className="line-through text-ink-400">{product.currency}{product.originalPrice}</span>
 <span className="ml-1">{t('homepage.productGrid.perMonth')}</span>
 </div>
 </div>
 </div>

 {/* 第3列：使用人数 */}
 <div className="xl:col-span-1 text-center">
 <div className="flex justify-center">
 <UserCountBadge count={product.features.users} size="sm" />
 </div>
 </div>

 {/* 第4列：登录方式 */}
 <div className="xl:col-span-2 text-center">
 <div className="text-sm text-ink-900 font-medium">
 {product.features.loginType === 'official' && t('homepage.productGrid.loginTypes.official')}
 {product.features.loginType === 'shared' && t('homepage.productGrid.loginTypes.shared')}
 {product.features.loginType === 'custom' && t('homepage.productGrid.loginTypes.custom')}
 </div>
 <div className="text-xs text-ink-500 mt-0.5">{t('homepage.productGrid.tableHeaders.loginType')}</div>
 </div>

 {/* 第5列：支持环境 */}
 <div className="xl:col-span-1 text-center">
 {product.features.loginType === 'official' ? (
 <>
 <MonitorSmartphone className="w-5 h-5 text-ink-700 mx-auto mb-1" />
 <div className="text-xs text-ink-600">{t('homepage.productGrid.platform.webAndApp')}</div>
 </>
 ) : (
 <>
 <Monitor className="w-5 h-5 text-ink-700 mx-auto mb-1" />
 <div className="text-xs text-ink-600">{t('homepage.productGrid.platform.webOnly')}</div>
 </>
 )}
 </div>

 {/* 第6列：操作按钮 */}
 <div className="xl:col-span-2 text-center">
 <button
 onClick={(e) => {
 e.stopPropagation();
 handleProductClick(product);
 }}
 className="inline-flex items-center justify-center gap-2 bg-ink-900 text-cream-50 px-5 py-2.5 rounded-lg font-medium text-sm hover:bg-ink-800 transition-colors duration-200 mx-auto"
 >
 <ShoppingCart className="w-4 h-4" />
 <span>
 {product.features.loginType === 'custom' ? t('homepage.productGrid.buttons.trial') : t('homepage.productGrid.buttons.details')}
 </span>
 </button>
 </div>
 </div>

 {/* 移动端和平板端卡片布局 */}
 <div className="xl:hidden">

 {/* 顶部：产品信息和价格 */}
 <div className="flex items-center justify-between mb-4">
 <div className="flex items-center gap-3">
 <div className="flex-shrink-0">
 <div className="relative w-12 h-12 sm:w-14 sm:h-14">
 <img
 src={product.image}
 alt={t(product.nameKey)}
 className="w-full h-full object-cover rounded-lg border border-ink-200"
 onError={(e) => {
 e.target.style.display = 'none';
 e.target.nextSibling.style.display = 'flex';
 }}
 />
 <div className="w-full h-full bg-cream-100 rounded-lg flex items-center justify-center border border-ink-200" style={{display: 'none'}}>
 <IconComponent className="w-6 h-6 text-ink-700" />
 </div>
 </div>
 </div>
 <div>
 <h3 className="font-semibold text-ink-900 group-hover:text-accent-600 transition-colors duration-200 text-sm sm:text-base">
 {t(product.nameKey)}
 </h3>
 <div className="text-xs text-ink-500 mt-1">
 {product.features.loginType === 'shared' ?
 (product.id === 'gpt-vip' ? t('homepage.productGrid.vipExclusive') : t('homepage.productGrid.privateConversation')) :
 (product.features.users === 1 ?
 t('homepage.productGrid.userCount.single') :
 t('homepage.productGrid.userCount.shared', { count: product.features.users })
 )
 }
 </div>
 </div>
 </div>

 {/* 价格区域 */}
 <div className="text-right">
 <div className="text-xs text-ink-400 line-through">{product.currency}{product.originalPrice}</div>
 <div className="text-lg font-serif text-ink-900">{product.currency}{product.price}</div>
 <div className="text-xs text-ink-500">{t('homepage.productGrid.perMonth')}</div>
 </div>
 </div>

 {/* 中部：详细信息网格 */}
 <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4 text-xs">

 <div className="bg-cream-100 rounded-lg p-2 border border-ink-200">
 <div className="text-ink-500 mb-1 text-center">{t('homepage.productGrid.tableHeaders.users')}</div>
 <div className="flex justify-center">
 <UserCountDisplay count={product.features.users} size="sm" />
 </div>
 </div>

 <div className="bg-cream-100 rounded-lg p-2 border border-ink-200">
 <div className="text-ink-500 mb-1">{t('homepage.productGrid.tableHeaders.loginType')}</div>
 <div className="font-medium text-ink-900">
 {product.features.loginType === 'official' && t('homepage.productGrid.loginTypes.official')}
 {product.features.loginType === 'shared' && t('homepage.productGrid.loginTypes.shared')}
 {product.features.loginType === 'custom' && t('homepage.productGrid.loginTypes.custom')}
 </div>
 </div>

 <div className="bg-cream-100 rounded-lg p-2 border border-ink-200">
 <div className="text-ink-500 mb-1">{t('homepage.productGrid.tableHeaders.platform')}</div>
 <div className="flex items-center gap-1">
 {product.features.loginType === 'official' ? (
 <>
 <MonitorSmartphone className="w-4 h-4 text-ink-700" />
 <span className="text-ink-900 font-medium">{t('homepage.productGrid.platform.webAndApp')}</span>
 </>
 ) : (
 <>
 <Monitor className="w-4 h-4 text-ink-700" />
 <span className="text-ink-900 font-medium">{t('homepage.productGrid.platform.webOnly')}</span>
 </>
 )}
 </div>
 </div>
 </div>

 {/* 底部：操作按钮 */}
 <div className="flex justify-center">
 <button
 onClick={(e) => {
 e.stopPropagation();
 handleProductClick(product);
 }}
 className="w-full inline-flex items-center justify-center gap-2 bg-ink-900 text-cream-50 py-3 rounded-lg font-medium hover:bg-ink-800 transition-colors duration-200"
 >
 <ShoppingCart className="w-5 h-5" />
 <span>{product.features.loginType === 'custom' ? t('homepage.productGrid.buttons.trial') : t('homepage.productGrid.buttons.details')}</span>
 <span className="text-base">→</span>
 </button>
 </div>
 </div>

 </div>
 </div>
 );
 })}
 </div>
 </div>
 );
 };

 return (
 <section id="products-section" className="py-20 lg:py-28 bg-cream-50">
 <div className="max-w-6xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
 
 {/* 产品展示 */}
 <div>
 {/* 官网账号 */}
 {renderProductList(
 officialProducts, 
 t('homepage.categories.official.name'), 
 t('homepage.categories.official.description'),
 'official'
 )}

 {/* 官网账号续费 */}
 {renderProductList(
 officialRenewalProducts, 
 t('homepage.categories.officialRenewal.name'), 
 t('homepage.categories.officialRenewal.description'),
 'official-renewal'
 )}

 {/* 官网共享账号 */}
 {renderProductList(
 officialSharedProducts, 
 t('homepage.categories.officialShared.name'), 
 t('homepage.categories.officialShared.description'),
 'official-shared'
 )}

 {/* 网页共享托管账号 */}
 {renderProductList(
 sharedProducts, 
 t('homepage.categories.shared.name'), 
 t('homepage.categories.shared.description'),
 'shared'
 )}

 {/* MonkeyAI */}
 {renderProductList(
 monkeyaiProducts, 
 t('homepage.categories.monkeyai.name'), 
 t('homepage.categories.monkeyai.description'),
 'monkeyai'
 )}

 {/* 如果所有产品都为空 */}
 {officialProducts.length === 0 && officialRenewalProducts.length === 0 && officialSharedProducts.length === 0 && sharedProducts.length === 0 && monkeyaiProducts.length === 0 && (
 <div className="flex items-center justify-center py-12">
 <div className="text-center">
 <div className="w-20 h-20 bg-cream-100 border border-ink-200 rounded-full flex items-center justify-center mx-auto mb-6">
 <ShoppingCart className="w-10 h-10 text-ink-400" />
 </div>
 <h3 className="font-serif text-xl text-ink-900 mb-2">{t('homepage.productGrid.noProducts')}</h3>
 <p className="text-ink-600">{t('homepage.productGrid.noProductsDesc')}</p>
 </div>
 </div>
 )}
 </div>

 </div>
 </section>
 );
}